/**
 * Veltrona — cart drawer: opens on add to cart, live quantity updates,
 * gift wrap, discount codes, order notes and recommendations.
 */
(() => {
  'use strict';

  const V = window.Veltrona;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section || 'cart-drawer';
      this.queue = this.queue || Promise.resolve();
      this.timers = new Map();
      this.dialog = this.querySelector('vt-dialog');

      this.onTrigger = (e) => {
        const trigger = e.target.closest('[data-cart-trigger]');
        if (!trigger || e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        this.open(trigger);
      };
      this.onAdded = async () => {
        await this.refresh();
        this.open();
      };
      document.addEventListener('click', this.onTrigger);
      document.addEventListener('veltrona:cart:added', this.onAdded);

      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      // Each line waits for its own pause in typing, so edits to different lines are all sent.
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[data-key]');
        if (!input) return;
        const key = input.dataset.key;
        clearTimeout(this.timers.get(key));
        this.timers.set(key, setTimeout(() => this.change(key, Number(input.value)), 350));
      });

      this.addEventListener('change', (e) => {
        const gift = e.target.closest('[data-gift-wrap]');
        if (gift) this.toggleGift(gift);
      });

      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-line]');
        if (remove) {
          e.preventDefault();
          remove.closest('.cd-item')?.classList.add('is-removing');
          this.change(remove.dataset.key, 0);
          return;
        }
        if (e.target.closest('[data-discount-apply]')) this.applyDiscount();
        const removeCode = e.target.closest('[data-discount-remove]');
        if (removeCode) this.applyDiscount(removeCode.dataset.discountRemove);
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) {
          e.preventDefault();
          this.applyDiscount();
        }
      });

      this.addEventListener(
        'input',
        V.utils.debounce((e) => {
          if (!e.target.matches('[data-cart-note]')) return;
          fetch(`${V.routes.cartUpdate}.js`, { ...V.utils.fetchConfig(), body: JSON.stringify({ note: e.target.value }) });
        }, 500)
      );
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      document.removeEventListener('veltrona:cart:added', this.onAdded);
    }

    open(trigger) {
      if (!this.isConnected) return;
      if (!this.dialog?.isOpen) this.dialog?.open(trigger);
      this.querySelector('vt-cart-upsell')?.load();
    }

    get inner() {
      return this.querySelector('[data-cart-inner]');
    }

    /** Swaps the drawer contents with a fresh render, keeping scroll and focus. */
    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-inner]');
      const host = doc.querySelector('cart-drawer');
      if (!fresh || !this.inner) return;
      const scroll = this.querySelector('[data-cart-scroll]')?.scrollTop || 0;
      const focusedId = document.activeElement?.id;
      const openDetails = Array.from(this.querySelectorAll('.cart-drawer__extra')).map((d) => d.open);
      this.inner.className = fresh.className;
      this.inner.innerHTML = fresh.innerHTML;
      this.inner.classList.add('is-refreshed');
      this.querySelectorAll('.cart-drawer__extra').forEach((d, i) => {
        if (openDetails[i]) d.open = true;
      });
      const scroller = this.querySelector('[data-cart-scroll]');
      if (scroller) scroller.scrollTop = scroll;
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
      if (host) this.updateHeader(Number(host.dataset.count), host.dataset.total);
      if (this.dialog?.isOpen) this.querySelector('vt-cart-upsell')?.load();
    }

    updateHeader(count, total) {
      V.Cart?.setCount(count);
      if (total) document.querySelectorAll('[data-cart-total]').forEach((el) => (el.textContent = total));
    }

    async refresh() {
      try {
        const res = await fetch(`${V.routes.root.replace(/\/$/, '')}/?sections=${this.sectionId}`);
        const data = await res.json();
        this.render(data[this.sectionId]);
      } catch (e) {
        /* Keep the current drawer if the refresh fails. */
      }
    }

    /** Changes a cart line by its item key, which stays valid while other requests are queued. */
    change(key, quantity) {
      this.queue = this.queue.then(() => this.request('change', { id: key, quantity }));
      return this.queue;
    }

    async request(endpoint, payload) {
      const item = payload.id ? this.querySelector(`.cd-item[data-key="${CSS.escape(payload.id)}"]`) : null;
      item?.classList.add('is-loading');
      this.classList.add('is-busy');
      const route = endpoint === 'add' ? V.routes.cartAdd : endpoint === 'update' ? V.routes.cartUpdate : V.routes.cartChange;
      try {
        const res = await fetch(`${route}.js`, {
          ...V.utils.fetchConfig(),
          body: JSON.stringify({ ...payload, sections: [this.sectionId], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || V.strings.cartError);
        if (data.sections) this.render(data.sections[this.sectionId]);
        else await this.refresh();
        return data;
      } catch (e) {
        item?.classList.remove('is-loading', 'is-removing');
        const error = this.querySelector('[data-cart-error]');
        if (error) {
          error.hidden = false;
          error.textContent = e.message;
        }
        return null;
      } finally {
        this.classList.remove('is-busy');
      }
    }

    toggleGift(input) {
      if (input.checked) {
        this.queue = this.queue.then(() => this.request('add', { items: [{ id: Number(input.dataset.giftWrap), quantity: 1 }] }));
      } else {
        const key = input.dataset.giftKey;
        if (key) this.change(key, 0);
      }
    }

    async applyDiscount(removeCode) {
      const box = this.querySelector('.cart-drawer__discount');
      const input = this.querySelector('[data-discount-input]');
      const error = this.querySelector('[data-discount-error]');
      const existing = (box?.dataset.codes || '').split(',').filter(Boolean);
      let codes;
      if (removeCode) {
        codes = existing.filter((c) => c !== removeCode);
      } else {
        const code = input?.value.trim();
        if (!code) return;
        if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
          input.value = '';
          return;
        }
        codes = [...existing, code];
      }
      if (error) error.hidden = true;
      const data = await (this.queue = this.queue.then(() => this.request('update', { discount: codes.join(',') })));
      if (!removeCode && data && input) {
        const added = (data.discount_codes || []).find((d) => d.code.toLowerCase() === codes[codes.length - 1].toLowerCase());
        if (added && !added.applicable) {
          const err = this.querySelector('[data-discount-error]');
          if (err) err.hidden = false;
        }
      }
    }
  }

  /** Lazily loads cart recommendations the first time the drawer opens. */
  class VtCartUpsell extends HTMLElement {
    async load() {
      if (this.loaded || !this.dataset.url) return;
      this.loaded = true;
      try {
        const res = await fetch(this.dataset.url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const content = doc.querySelector('[data-cart-upsell-content]');
        if (!content || !content.querySelector('li')) return;
        this.innerHTML = content.innerHTML;
        this.classList.add('is-loaded');
        const track = this.querySelector('[data-upsell-track]');
        const step = () => (track?.firstElementChild?.offsetWidth || 200) + 12;
        this.querySelector('[data-upsell-prev]')?.addEventListener('click', () => track?.scrollBy({ left: -step(), behavior: 'smooth' }));
        this.querySelector('[data-upsell-next]')?.addEventListener('click', () => track?.scrollBy({ left: step(), behavior: 'smooth' }));
      } catch (e) {
        /* Recommendations are optional. */
      }
    }
  }

  if (!customElements.get('vt-cart-upsell')) customElements.define('vt-cart-upsell', VtCartUpsell);
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
})();
